import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { 
  CreditCard, Building2, Wifi, Store, Globe, Users, 
  Phone, Mail, MapPin, ChevronRight, Menu, X, Check,
  Shield, Clock, Headphones, Briefcase, Zap, PiggyBank,
  Calculator, Bolt, Smartphone, Wrench, Monitor, BarChart3,
  MessageSquare, FileText, TrendingUp
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Navigation Component
const Navbar = ({ scrolled }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const navLinks = [
    { name: "Home", href: "#home" },
    { name: "About Us", href: "#about" },
    { name: "Our Services", href: "#services" },
    { name: "Contact Us", href: "#contact" }
  ];

  return (
    <nav 
      data-testid="navbar"
      className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#home" className="flex items-center" data-testid="logo-link">
            <img 
              src="/logo.svg" 
              alt="AAJ Tech Group" 
              className="h-14 w-auto"
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link, index) => (
              <a
                key={link.name}
                href={link.href}
                data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
                className="nav-link text-[#0B1120] font-medium hover:text-[#D4AF37] px-2"
              >
                {link.name}
              </a>
            ))}
            <a 
              href="tel:02084323235" 
              className="btn-primary flex items-center gap-2"
              data-testid="nav-call-btn"
            >
              <Phone size={18} />
              Call Us
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-[#0B1120] p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-100 py-6" data-testid="mobile-menu">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="block py-3 px-4 text-[#0B1120] hover:text-[#D4AF37] font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.name}
              </a>
            ))}
            <div className="px-4 pt-4">
              <a href="tel:02084323235" className="btn-primary w-full flex items-center justify-center gap-2">
                <Phone size={18} />
                Call Us
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section 
      id="home" 
      data-testid="hero-section"
      className="min-h-screen bg-[#0B1120] relative overflow-hidden"
    >
      {/* Background Image Overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center opacity-20"
        style={{ backgroundImage: `url('https://images.unsplash.com/photo-1740595193473-f66d71b5e176?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjB0ZWFtJTIwbG9uZG9uJTIwb2ZmaWNlJTIwYnVzaW5lc3MlMjBtZWV0aW5nfGVufDB8fHx8MTc3MDkwMTY2OXww&ixlib=rb-4.1.0&q=85')` }}
      />
      
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pt-32 pb-20 min-h-screen flex items-center">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="animate-fade-in-up">
            <p className="text-[#D4AF37] font-semibold tracking-widest uppercase mb-4">
              AAJ Tech Group
            </p>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6">
              Everything Your Business Needs –{" "}
              <span className="gradient-text">In One Place</span>
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed mb-10 max-w-xl">
              We support UK businesses with everything they need to start, grow, and scale – 
              from card payment solutions and business finance to shop fitting, broadband, 
              VoIP, energy and full business setup.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href="#contact" 
                className="btn-primary text-center"
                data-testid="hero-get-started-btn"
              >
                Get Started Today
              </a>
              <a 
                href="#services" 
                className="btn-secondary text-center flex items-center justify-center gap-2"
                data-testid="hero-services-btn"
              >
                Our Services
                <ChevronRight size={18} />
              </a>
            </div>
          </div>

          {/* Right - Stats */}
          <div className="grid grid-cols-2 gap-6 animate-fade-in-up delay-300">
            {[
              { number: "10+", label: "Business Services" },
              { number: "UK", label: "Based Support" },
              { number: "24/7", label: "Customer Care" },
              { number: "100%", label: "Satisfaction" }
            ].map((stat, index) => (
              <div 
                key={index}
                className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 text-center"
              >
                <p className="text-4xl font-bold text-[#D4AF37] mb-2">{stat.number}</p>
                <p className="text-slate-400 font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tagline Bar */}
      <div className="absolute bottom-0 left-0 right-0 bg-[#D4AF37] py-4">
        <p className="text-[#0B1120] text-center font-bold text-lg">
          Grow with us. Save more. Succeed faster.
        </p>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="about" data-testid="about-section" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div>
            <p className="text-[#D4AF37] font-semibold tracking-widest uppercase mb-4">
              About Us
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1120] mb-6">
              Your All-in-One Business Solutions Partner
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              AAJ Tech Group is a UK-based business solutions provider helping entrepreneurs 
              and companies start, grow, and scale with confidence.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              We bring together essential services under one roof – from card payment solutions 
              and business finance to shop fitting, connectivity, and technical installations. 
              Our goal is simple: to make running a business easier, more affordable, and more efficient.
            </p>
            <div className="space-y-4">
              {[
                "Honest advice and transparent pricing",
                "Fast setup and ongoing support",
                "Tailored solutions for your business needs"
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                    <Check size={14} className="text-[#D4AF37]" />
                  </div>
                  <p className="text-slate-700 font-medium">{item}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right - How We Operate */}
          <div className="bg-[#0B1120] p-10 relative">
            <div className="absolute top-0 right-0 w-20 h-20 bg-[#D4AF37]" />
            <h3 className="text-2xl font-bold text-white mb-6">How We Operate</h3>
            <p className="text-slate-300 leading-relaxed mb-8">
              We work directly with you and via carefully selected and vetted third party 
              providers to provide you the best possible service in selected functionalities. 
              You can pick and choose the service/s that you may require.
            </p>
            <div className="border-t border-white/20 pt-6">
              <p className="text-[#D4AF37] font-semibold text-lg">
                AAJ Tech Group is your all-in-one business solutions partner.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Data with Full Details
const servicesData = [
  {
    icon: CreditCard,
    title: "Card Payment Solutions",
    description: "Take payments in-store, online & over the phone with competitive rates.",
    features: ["No contract options", "Free Sunmi terminal", "Faster payouts", "Lower fees"],
    large: true,
    fullDetails: {
      headline: "Take Payments In-Store, Online & Over the Phone",
      intro: "Accept card payments the way your customers prefer – securely, quickly, and cost-effectively.",
      paymentMethods: [
        "In-store card terminals",
        "Online payments",
        "Pay-by-link",
        "MOTO (Mail Order / Telephone Order)"
      ],
      offerings: [
        "No-contract and flexible pricing options",
        "Free Sunmi terminal (subject to eligibility)",
        "Faster payouts and instant settlement options",
        "Competitive transaction rates",
        "Simple onboarding and fast setup",
        "Ongoing support and account management",
        "E-POS integrations and loyalty solutions",
        "Access to business funding linked to card turnover"
      ],
      cta: "Get set up quickly and start taking payments with confidence. Switch today and start saving on transaction fees."
    }
  },
  {
    icon: PiggyBank,
    title: "Business Finance & Protection",
    description: "Funding & protection to help you grow your business.",
    features: ["Business loans", "Commercial mortgages", "Business insurance", "Life insurance"],
    large: true,
    fullDetails: {
      headline: "Funding and Protection to Help You Grow",
      intro: "Whether you're launching, expanding, or stabilising cash flow, we help you access the right finance and protection solutions for your situation.",
      services: [
        "Business loans",
        "Merchant cash advance",
        "Commercial mortgages",
        "Auction finance",
        "Business insurance",
        "Life insurance",
        "Private medical insurance"
      ],
      note: "We guide you through your options clearly and connect you with suitable lenders and insurers based on your business profile.",
      disclaimer: "All finance and insurance services are subject to status and provider criteria."
    }
  },
  {
    icon: Bolt,
    title: "Business Utilities & Connectivity",
    description: "Power, internet & phones – sorted.",
    features: ["Gas & electricity", "Broadband", "VoIP phones", "Mobile SIMs"],
    fullDetails: {
      headline: "Power, Internet & Phones — Sorted",
      intro: "Reliable utilities and connectivity are essential to running a smooth operation. We help you secure better-value, dependable services with minimal disruption.",
      services: [
        "Business gas & electricity",
        "Business broadband",
        "VoIP phone systems",
        "Business mobile SIMs",
        "Business bank accounts"
      ],
      cta: "We compare leading providers, manage the switch, and ensure your business stays connected without downtime."
    }
  },
  {
    icon: Store,
    title: "Shop Fitting & Business Setup",
    description: "From empty unit to fully open business.",
    features: ["Shelving & counters", "CCTV", "Air conditioning", "Electrical"],
    fullDetails: {
      headline: "From Empty Unit to Fully Open Business",
      intro: "We help turn your premises into a fully operational, customer-ready space — including energy-efficient and future-ready solutions.",
      services: [
        "Shelving, counters & ceilings",
        "Chiller and cold/freezer rooms",
        "Air conditioning installation",
        "CCTV and security systems",
        "Electrical wiring & lighting",
        "Solar panel installation",
        "EV charging point installation (Home, Commercial & Workplace)",
        "Audio-visual systems",
        "Office and back-of-house setup",
        "Ongoing maintenance & aftercare"
      ],
      idealFor: "Ideal for shops, off-licences, takeaways, restaurants, salons, offices, and retail outlets looking to reduce energy costs and future-proof their premises."
    }
  },
  {
    icon: Monitor,
    title: "App & Web Creation",
    description: "Digital products that drive results.",
    features: ["Mobile apps", "Websites", "E-commerce", "UI/UX design"],
    fullDetails: {
      headline: "Build. Launch. Grow.",
      intro: "We create digital products and marketing ecosystems that don't just look good — they drive results.",
      sections: [
        {
          title: "App Development",
          items: ["Custom mobile apps (iOS, Android & cross-platform)", "Scalable, secure architecture", "User-focused UI/UX design"]
        },
        {
          title: "Website Design & Development",
          items: ["High-performance, responsive websites", "SEO-optimised and conversion-focused builds", "E-commerce, corporate & custom platforms"]
        }
      ],
      cta: "End-to-end digital solutions tailored to your growth goals."
    }
  },
  {
    icon: BarChart3,
    title: "Digital Marketing",
    description: "Holistic marketing solutions.",
    features: ["SEO & SEM", "Social media", "Content strategy", "Analytics"],
    fullDetails: {
      headline: "Holistic Digital Marketing",
      intro: "We design powerful digital experiences and marketing ecosystems that don't just look good—they perform.",
      services: [
        "SEO, SEM & paid advertising",
        "Social media management & paid campaigns",
        "Content strategy & branding",
        "Marketing automation & analytics",
        "Continuous optimisation and reporting"
      ],
      benefits: [
        "End-to-end digital solutions under one roof",
        "Strategy-driven, data-backed execution",
        "Custom solutions tailored to your business goals",
        "Transparent processes and measurable results"
      ]
    }
  },
  {
    icon: Building2,
    title: "Business Bank Accounts",
    description: "Get the right banking setup for your business.",
    features: ["Account setup", "Payment solutions", "Banking support"],
    fullDetails: {
      headline: "Business Banking Made Simple",
      intro: "We help you find and set up the right business bank account for your needs.",
      services: [
        "Business current accounts",
        "Merchant accounts",
        "Multi-currency accounts",
        "Payment gateway integration",
        "Banking support and guidance"
      ],
      cta: "Get set up with the right banking partner for your business needs."
    }
  },
  {
    icon: Calculator,
    title: "Accounting Services",
    description: "Professional accounting and bookkeeping.",
    features: ["Bookkeeping", "VAT returns", "Payroll", "Management accounts"],
    fullDetails: {
      headline: "Professional Accounting & Bookkeeping",
      intro: "Keep your finances in order with professional accounting support.",
      services: [
        "Bookkeeping & accounting support",
        "Invoicing, payroll & VAT assistance",
        "Management reporting & financial admin",
        "Tax planning and compliance",
        "Year-end accounts preparation"
      ],
      cta: "Focus on your business while we handle the numbers."
    }
  },
  {
    icon: Users,
    title: "BPO Solutions",
    description: "Streamline operations, reduce costs, scale faster.",
    features: ["Customer support", "Back-office", "Lead generation", "Process optimization"],
    fullDetails: {
      headline: "Streamline Operations. Reduce Costs. Scale Faster.",
      intro: "Our Business Process Outsourcing (BPO) solutions allow you to focus on growth while we manage the operational workload that keeps your business running smoothly.",
      sections: [
        {
          title: "Customer Support & Experience",
          items: ["Voice, email & live chat support", "CRM management and customer retention services"]
        },
        {
          title: "Back-Office & Accounting Services",
          items: ["Data entry & data management", "Bookkeeping & accounting support", "Invoicing, payroll & VAT assistance", "Management reporting & financial admin", "Order processing & administrative services"]
        },
        {
          title: "Sales & Lead Support",
          items: ["Lead generation and qualification", "Appointment setting", "Sales support teams"]
        },
        {
          title: "Process Optimisation",
          items: ["Workflow analysis and automation", "Cost reduction strategies", "Scalable operational models"]
        }
      ],
      benefits: [
        "Reduced operating costs without compromising quality",
        "Skilled, dedicated teams aligned to your business",
        "Faster turnaround times and improved productivity",
        "Secure, compliant, and reliable processes"
      ]
    }
  },
  {
    icon: Shield,
    title: "Mortgages & Insurance",
    description: "Comprehensive protection for you and your business.",
    features: ["Commercial mortgages", "Auction finance", "Private medical"],
    fullDetails: {
      headline: "Comprehensive Protection for You & Your Business",
      intro: "We help you secure the right protection and property finance solutions.",
      services: [
        "Commercial mortgages",
        "Auction finance",
        "Business insurance",
        "Life insurance",
        "Private medical insurance"
      ],
      note: "Finance and insurance services are subject to status and provider criteria.",
      cta: "Get the protection your business needs to thrive."
    }
  }
];

// Service Detail Modal Component
const ServiceModal = ({ service, isOpen, onClose }) => {
  if (!isOpen || !service) return null;

  const Icon = service.icon;
  const details = service.fullDetails;

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      data-testid="service-modal"
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-[#0B1120]/80 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <div className="relative bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-fade-in-up">
        {/* Header */}
        <div className="sticky top-0 bg-[#0B1120] p-6 flex items-center justify-between z-10">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#D4AF37]/20 flex items-center justify-center">
              <Icon size={24} className="text-[#D4AF37]" />
            </div>
            <h2 className="text-2xl font-bold text-white">{service.title}</h2>
          </div>
          <button 
            onClick={onClose}
            className="text-white hover:text-[#D4AF37] transition-colors p-2"
            data-testid="modal-close-btn"
          >
            <X size={24} />
          </button>
        </div>

        {/* Body */}
        <div className="p-8">
          {details.headline && (
            <h3 className="text-2xl font-bold text-[#0B1120] mb-4">{details.headline}</h3>
          )}
          
          {details.intro && (
            <p className="text-lg text-slate-600 leading-relaxed mb-6">{details.intro}</p>
          )}

          {/* Payment Methods (for Card Payments) */}
          {details.paymentMethods && (
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-[#0B1120] mb-3">Payment Methods Supported:</h4>
              <ul className="grid grid-cols-2 gap-2">
                {details.paymentMethods.map((method, index) => (
                  <li key={index} className="flex items-center gap-2 text-slate-600">
                    <Check size={16} className="text-[#D4AF37]" />
                    {method}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Offerings */}
          {details.offerings && (
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-[#0B1120] mb-3">What We Offer:</h4>
              <ul className="space-y-2">
                {details.offerings.map((item, index) => (
                  <li key={index} className="flex items-start gap-2 text-slate-600">
                    <Check size={16} className="text-[#D4AF37] mt-1 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Services List */}
          {details.services && (
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-[#0B1120] mb-3">Our Services:</h4>
              <ul className="space-y-2">
                {details.services.map((item, index) => (
                  <li key={index} className="flex items-start gap-2 text-slate-600">
                    <Check size={16} className="text-[#D4AF37] mt-1 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Sections (for services with multiple categories) */}
          {details.sections && (
            <div className="space-y-6 mb-6">
              {details.sections.map((section, index) => (
                <div key={index} className="bg-[#F8F9FA] p-6">
                  <h4 className="text-lg font-semibold text-[#0B1120] mb-3">{section.title}</h4>
                  <ul className="space-y-2">
                    {section.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start gap-2 text-slate-600">
                        <Check size={16} className="text-[#D4AF37] mt-1 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {/* Benefits */}
          {details.benefits && (
            <div className="mb-6 bg-[#0B1120]/5 p-6">
              <h4 className="text-lg font-semibold text-[#0B1120] mb-3">The Value We Deliver:</h4>
              <ul className="space-y-2">
                {details.benefits.map((item, index) => (
                  <li key={index} className="flex items-start gap-2 text-slate-600">
                    <Check size={16} className="text-[#D4AF37] mt-1 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Ideal For */}
          {details.idealFor && (
            <p className="text-slate-600 italic mb-6 p-4 border-l-4 border-[#D4AF37] bg-[#D4AF37]/5">
              {details.idealFor}
            </p>
          )}

          {/* Note/Disclaimer */}
          {details.note && (
            <p className="text-sm text-slate-500 mb-6">{details.note}</p>
          )}
          
          {details.disclaimer && (
            <p className="text-sm text-slate-500 italic mb-6">{details.disclaimer}</p>
          )}

          {/* CTA */}
          {details.cta && (
            <div className="bg-[#D4AF37]/10 p-6 border-l-4 border-[#D4AF37]">
              <p className="text-[#0B1120] font-medium">{details.cta}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-slate-100 p-6 flex justify-between items-center">
          <button 
            onClick={onClose}
            className="btn-secondary"
            data-testid="modal-back-btn"
          >
            Back to Services
          </button>
          <a 
            href="#contact" 
            onClick={onClose}
            className="btn-primary flex items-center gap-2"
            data-testid="modal-enquire-btn"
          >
            Enquire Now
            <ChevronRight size={18} />
          </a>
        </div>
      </div>
    </div>
  );
};

// Services Section
const ServicesSection = () => {
  const [selectedService, setSelectedService] = useState(null);

  return (
    <section id="services" data-testid="services-section" className="py-24 bg-[#F8F9FA]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16">
          <p className="text-[#D4AF37] font-semibold tracking-widest uppercase mb-4">
            Our Services
          </p>
          <h2 className="text-4xl md:text-5xl font-bold text-[#0B1120] mb-6">
            What We Do
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            We offer a comprehensive range of business services to help you start, grow, and scale.
            <span className="block mt-2 text-[#D4AF37] font-medium">Click any service for more details.</span>
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {servicesData.map((service, index) => {
            const Icon = service.icon;
            return (
              <div
                key={index}
                data-testid={`service-card-${index}`}
                onClick={() => setSelectedService(service)}
                className={`service-card bg-white border border-slate-100 p-8 flex flex-col cursor-pointer group ${
                  service.large ? 'lg:col-span-2' : ''
                }`}
              >
                <div className="w-14 h-14 bg-[#D4AF37]/10 flex items-center justify-center mb-6 group-hover:bg-[#D4AF37] transition-colors duration-300">
                  <Icon size={28} className="text-[#D4AF37] group-hover:text-[#0B1120]" />
                </div>
                <h3 className="text-xl font-bold text-[#0B1120] mb-3">{service.title}</h3>
                <p className="text-slate-600 mb-6 flex-grow">{service.description}</p>
                <ul className="space-y-2 mb-4">
                  {service.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-center gap-2 text-sm text-slate-500">
                      <Check size={14} className="text-[#D4AF37]" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <div className="flex items-center gap-2 text-[#D4AF37] font-semibold text-sm group-hover:gap-3 transition-all duration-300">
                  Learn More
                  <ChevronRight size={16} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Service Detail Modal */}
      <ServiceModal 
        service={selectedService} 
        isOpen={!!selectedService} 
        onClose={() => setSelectedService(null)} 
      />
    </section>
  );
};

// Why Choose Us Section
const WhyChooseSection = () => {
  const reasons = [
    { icon: Building2, title: "All Services Under One Roof", description: "No need to juggle multiple providers" },
    { icon: Headphones, title: "One Point of Contact", description: "Dedicated support for all your needs" },
    { icon: PiggyBank, title: "Competitive Pricing", description: "Through trusted partners" },
    { icon: Zap, title: "Fast Setup", description: "Smooth switching and quick deployment" },
    { icon: Briefcase, title: "Tailored Solutions", description: "For small and growing UK businesses" },
    { icon: Users, title: "Friendly Local Support", description: "UK-based team ready to help" }
  ];

  return (
    <section data-testid="why-choose-section" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
          {/* Left - Sticky Header */}
          <div className="lg:sticky lg:top-32 lg:self-start">
            <p className="text-[#D4AF37] font-semibold tracking-widest uppercase mb-4">
              Why Choose Us
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1120] mb-6">
              Why Choose AAJ Tech Group
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              We make running your business simpler.
            </p>
          </div>

          {/* Right - Features Grid */}
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-8">
            {reasons.map((reason, index) => {
              const Icon = reason.icon;
              return (
                <div 
                  key={index} 
                  className="group p-6 border border-slate-100 hover:border-[#D4AF37]/30 transition-colors duration-300"
                  data-testid={`why-choose-${index}`}
                >
                  <div className="w-12 h-12 bg-[#0B1120] flex items-center justify-center mb-4 group-hover:bg-[#D4AF37] transition-colors duration-300">
                    <Icon size={24} className="text-[#D4AF37] group-hover:text-[#0B1120]" />
                  </div>
                  <h3 className="text-lg font-bold text-[#0B1120] mb-2">{reason.title}</h3>
                  <p className="text-slate-600">{reason.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    service_interest: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const services = [
    "Card Payment Solutions",
    "Business Finance",
    "Business Utilities",
    "Shop Fitting & Setup",
    "App & Web Creation",
    "Digital Marketing",
    "BPO Solutions",
    "Accounting Services",
    "Other"
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.success) {
        setSubmitted(true);
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          service_interest: '',
          message: ''
        });
      }
    } catch (err) {
      setError('Failed to submit form. Please try again or call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 bg-[#F8F9FA]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left - Contact Info */}
          <div>
            <p className="text-[#D4AF37] font-semibold tracking-widest uppercase mb-4">
              Contact Us
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1120] mb-6">
              Get Started Today
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-10">
              Whether you're opening a new business or upgrading your current setup, 
              we're ready to help.
            </p>

            <div className="space-y-6">
              <a 
                href="tel:02084323235" 
                className="flex items-center gap-4 group"
                data-testid="contact-phone"
              >
                <div className="w-14 h-14 bg-[#0B1120] flex items-center justify-center group-hover:bg-[#D4AF37] transition-colors duration-300">
                  <Phone size={24} className="text-[#D4AF37] group-hover:text-[#0B1120]" />
                </div>
                <div>
                  <p className="text-sm text-slate-500 uppercase tracking-wide">Call Us</p>
                  <p className="text-xl font-bold text-[#0B1120]">0208 432 3235</p>
                </div>
              </a>

              <a 
                href="mailto:info@aajtech.group" 
                className="flex items-center gap-4 group"
                data-testid="contact-email"
              >
                <div className="w-14 h-14 bg-[#0B1120] flex items-center justify-center group-hover:bg-[#D4AF37] transition-colors duration-300">
                  <Mail size={24} className="text-[#D4AF37] group-hover:text-[#0B1120]" />
                </div>
                <div>
                  <p className="text-sm text-slate-500 uppercase tracking-wide">Email</p>
                  <p className="text-xl font-bold text-[#0B1120]">info@aajtech.group</p>
                </div>
              </a>

              <a 
                href="https://www.aajtech.group" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-4 group"
                data-testid="contact-website"
              >
                <div className="w-14 h-14 bg-[#0B1120] flex items-center justify-center group-hover:bg-[#D4AF37] transition-colors duration-300">
                  <Globe size={24} className="text-[#D4AF37] group-hover:text-[#0B1120]" />
                </div>
                <div>
                  <p className="text-sm text-slate-500 uppercase tracking-wide">Website</p>
                  <p className="text-xl font-bold text-[#0B1120]">www.aajtech.group</p>
                </div>
              </a>
            </div>
          </div>

          {/* Right - Contact Form */}
          <div className="bg-white p-10 shadow-lg" data-testid="contact-form-container">
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Check size={40} className="text-[#D4AF37]" />
                </div>
                <h3 className="text-2xl font-bold text-[#0B1120] mb-4">Thank You!</h3>
                <p className="text-slate-600 mb-6">
                  We've received your enquiry and will be in touch within 24 hours.
                </p>
                <button 
                  onClick={() => setSubmitted(false)}
                  className="btn-secondary"
                  data-testid="send-another-btn"
                >
                  Send Another Enquiry
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <h3 className="text-2xl font-bold text-[#0B1120] mb-6">Send Us a Message</h3>
                
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 mb-6" data-testid="form-error">
                    {error}
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="input-field"
                      placeholder="Your name"
                      data-testid="input-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Company</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="input-field"
                      placeholder="Company name"
                      data-testid="input-company"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="input-field"
                      placeholder="your@email.com"
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="input-field"
                      placeholder="Phone number"
                      data-testid="input-phone"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Service Interest *</label>
                  <select
                    name="service_interest"
                    value={formData.service_interest}
                    onChange={handleChange}
                    required
                    className="input-field"
                    data-testid="select-service"
                  >
                    <option value="">Select a service...</option>
                    {services.map((service, index) => (
                      <option key={index} value={service}>{service}</option>
                    ))}
                  </select>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Message *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="input-field resize-none"
                    placeholder="Tell us about your business needs..."
                    data-testid="input-message"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="submit-btn"
                >
                  {submitting ? (
                    <>
                      <span className="animate-spin">⏳</span>
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <ChevronRight size={18} />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-[#0B1120] py-16">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Logo & Tagline */}
          <div className="lg:col-span-2">
            <img src="/logo-footer.svg" alt="AAJ Tech Group" className="h-16 mb-6" />
            <p className="text-slate-400 leading-relaxed max-w-md mb-6">
              We support UK businesses with everything they need to start, grow, and scale. 
              Your all-in-one business solutions partner.
            </p>
            <p className="text-[#D4AF37] font-semibold">
              Grow with us. Save more. Succeed faster.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-bold mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {["Home", "About Us", "Our Services", "Contact Us"].map((link, index) => (
                <li key={index}>
                  <a 
                    href={`#${link.toLowerCase().replace(' ', '-')}`}
                    className="text-slate-400 hover:text-[#D4AF37] transition-colors duration-300"
                  >
                    {link}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-bold mb-6">Contact</h4>
            <ul className="space-y-4">
              <li>
                <a href="tel:02084323235" className="flex items-center gap-3 text-slate-400 hover:text-[#D4AF37]">
                  <Phone size={18} />
                  0208 432 3235
                </a>
              </li>
              <li>
                <a href="mailto:info@aajtech.group" className="flex items-center gap-3 text-slate-400 hover:text-[#D4AF37]">
                  <Mail size={18} />
                  info@aajtech.group
                </a>
              </li>
              <li>
                <a href="https://www.aajtech.group" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-slate-400 hover:text-[#D4AF37]">
                  <Globe size={18} />
                  www.aajtech.group
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} AAJ Tech Group. All rights reserved.
          </p>
          <div className="flex gap-6 text-sm">
            <a href="#" className="text-slate-500 hover:text-[#D4AF37]">Privacy Policy</a>
            <a href="#" className="text-slate-500 hover:text-[#D4AF37]">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="App" data-testid="app-container">
      <Navbar scrolled={scrolled} />
      <HeroSection />
      <AboutSection />
      <ServicesSection />
      <WhyChooseSection />
      <ContactSection />
      <Footer />
    </div>
  );
}

export default App;
